import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { AchievementCelebration, openBlueskyShare } from "../../utils/achievementCelebration";
import * as analytics from "../../utils/analytics";
import styles from "./achievementCelebrationDialog.module.scss";

type AchievementCelebrationDialogProps = {
    achievement: AchievementCelebration;
    continueTo: string;
    onDismiss: () => void;
};

export default function AchievementCelebrationDialog({ achievement, continueTo, onDismiss }: AchievementCelebrationDialogProps) {
    const { t } = useTranslation("common");
    const dialogRef = useRef<HTMLDivElement>(null);
    const continueRef = useRef<HTMLAnchorElement>(null);
    const [shareError, setShareError] = useState(false);
    const featureTitle = achievement.kind === "feature"
        ? t(`achievementDialog.features.${achievement.discoveryId}.title`, { defaultValue: achievement.title })
        : achievement.title;
    const continueLabel = achievement.kind === "level"
        ? t("achievementDialog.actions.level")
        : achievement.kind === "achievement"
            ? t("achievementDialog.actions.achievement")
            : achievement.kind === "feature"
                ? t("achievementDialog.actions.feature", { title: featureTitle })
                : t("achievementDialog.continueLearning");

    const dismiss = useCallback(() => {
        analytics.track("achievement_celebration_dismissed", { achievement_type: achievement.kind });
        onDismiss();
    }, [achievement.kind, onDismiss]);

    useEffect(() => {
        continueRef.current?.focus();
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") dismiss();
            if (event.key !== "Tab" || !dialogRef.current) return;
            const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>("a[href], button:not([disabled])"));
            if (!focusable.length) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [dismiss]);

    function share() {
        analytics.track("achievement_bluesky_share_selected", { achievement_type: achievement.kind });
        setShareError(!openBlueskyShare(achievement));
    }

    return (
        <div className={styles.backdrop} role="presentation">
            <div className={styles.dialog} role="dialog" aria-modal="true" aria-labelledby="achievement-title" aria-describedby="achievement-message" ref={dialogRef}>
                <p className={styles.eyebrow}>{t("achievementDialog.unlocked")}</p>
                <h2 id="achievement-title">{achievement.kind === "level" ? t("achievementDialog.levelTitle", { level: achievement.level }) : featureTitle}</h2>
                <p id="achievement-message">{achievement.kind === "feature" ? t(`achievementDialog.features.${achievement.discoveryId}.message`, { defaultValue: achievement.message }) : t(`achievementDialog.messages.${achievement.kind}`, { level: achievement.level, defaultValue: achievement.message })}</p>
                {achievement.xp !== undefined && <p className={styles.xp}>+{achievement.xp} XP</p>}
                <div className={styles.actions}>
                    <Link ref={continueRef} to={continueTo} className={styles.continue} onClick={() => {
                        analytics.track("achievement_continue_learning_selected", { achievement_type: achievement.kind });
                        onDismiss();
                    }}>{continueLabel}</Link>
                    {achievement.shareable && <button type="button" className={styles.share} onClick={share}>{t("achievementDialog.shareOnBluesky")}</button>}
                    <button type="button" className={styles.dismiss} onClick={dismiss}>{t("achievementDialog.notNow")}</button>
                </div>
                {shareError && <p className={styles.error} role="status">{t("achievementDialog.shareError")}</p>}
            </div>
        </div>
    );
}
